import { useRef, useState, type CSSProperties } from 'react';
import { ComparisonCard } from './ComparisonCard';
import { COMPARISON_DATA } from './comparisonData';
import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { ChevronLeft, ChevronRight } from '../icons';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ACCENT, BORDER, SURFACE, TEXT_PRIMARY } from '../../tokens';

/** Roughly one card plus its gap. */
const SCROLL_STEP = 320;

/** Horizontally scrolling rail of comparison cards. */
function ComparisonAccordion() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateBounds = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 0);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  };

  const scroll = (direction: 'left' | 'right'): void => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -SCROLL_STEP : SCROLL_STEP, behavior: 'smooth' });
    setTimeout(updateBounds, 350);
  };

  const btnStyle = (disabled: boolean): CSSProperties => ({
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: `1px solid ${disabled ? 'rgba(255,255,255,0.04)' : BORDER}`,
    background: SURFACE,
    color: disabled ? '#333' : TEXT_PRIMARY,
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color 0.2s, color 0.2s',
    opacity: disabled ? 0.4 : 1,
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 20 }}>
        <button
          type="button"
          aria-label="Scroll left"
          disabled={atStart}
          onClick={() => scroll('left')}
          style={btnStyle(atStart)}
          onMouseEnter={(e) => {
            if (!atStart) e.currentTarget.style.borderColor = ACCENT;
          }}
          onMouseLeave={(e) => {
            if (!atStart) e.currentTarget.style.borderColor = BORDER;
          }}
        >
          <ChevronLeft size={20} />
        </button>

        <button
          type="button"
          aria-label="Scroll right"
          disabled={atEnd}
          onClick={() => scroll('right')}
          style={btnStyle(atEnd)}
          onMouseEnter={(e) => {
            if (!atEnd) e.currentTarget.style.borderColor = ACCENT;
          }}
          onMouseLeave={(e) => {
            if (!atEnd) e.currentTarget.style.borderColor = BORDER;
          }}
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div
        ref={scrollRef}
        onScroll={updateBounds}
        className="no-scrollbar"
        style={{
          display: 'flex',
          gap: 20,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          paddingBottom: 20,
        }}
      >
        {COMPARISON_DATA.map((item) => (
          <ComparisonCard key={item.category} item={item} />
        ))}
      </div>
    </div>
  );
}

export function ComparisonSection() {
  const isMobile = useIsMobile();

  return (
    <section style={{ padding: isMobile ? '60px 20px' : '100px 64px', background: 'rgba(6,6,6,0.88)' }}>
      <div style={{ maxWidth: 940, margin: '0 auto' }}>
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <SectionLabel number="06">What Makes This Different</SectionLabel>
            <SectionH2 style={{ marginBottom: 16 }}>Cheaper than both. And you keep it.</SectionH2>
          </div>
        </FadeUp>

        <FadeUp delay={80}>
          <ComparisonAccordion />
        </FadeUp>
      </div>
    </section>
  );
}
